import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface PasswordStrength {
  score: number;
  strength: string;
  valid: boolean;
  errors: string[];
}

export class PasswordValidator {

  /**
   * Custom validator for password complexity
   * Requirements:
   * - Minimum 8 characters
   * - At least one uppercase letter
   * - At least one lowercase letter
   * - At least one number
   * - At least one special character
   */
  static strongPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.value;

      if (!password) {
        return null; // Don't validate empty values to allow optional controls
      }

      const validation = PasswordValidator.validatePassword(password);

      if (!validation.valid) {
        return {
          strongPassword: {
            valid: false,
            errors: validation.errors
          }
        };
      }

      return null;
    };
  }

  /**
   * Validates password against complexity requirements
   * @param password - The password to validate
   * @returns Object with validation results
   */
  static validatePassword(password: string): PasswordStrength {
    const errors: string[] = [];

    if (!password) {
      errors.push('Password is required');
      return { valid: false, errors, score: 0, strength: 'None' };
    }

    // Minimum length check
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }

    // Maximum length check (prevent DoS)
    if (password.length > 128) {
      errors.push('Must not exceed 128 characters');
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }

    // Check for number
    if (!/[0-9]/.test(password)) {
      errors.push('One number');
    }

    // Check for special character
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('One special character (!@#$%^&*()_+-=[]{}|,.<>?)');
    }

    // Calculate strength score
    let score = 0;
    if (password.length >= 8) { score++; }
    if (password.length >= 12) { score++; }
    if (password.length >= 16) { score++; }
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) { score++; }
    if (/[0-9]/.test(password)) { score++; }
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) { score++; }

    let strength = 'Weak';
    if (score >= 6) {
      strength = 'Very Strong';
    } else if (score >= 4) {
      strength = 'Strong';
    } else if (score >= 2) {
      strength = 'Medium';
    }

    return {
      valid: errors.length === 0,
      errors,
      score: Math.min(score, 10),
      strength
    };
  }

  /**
   * Returns password requirements as a formatted message
   */
  static getRequirementsMessage(): string {
    return 'Password must be at least 8 characters long and contain at least one uppercase letter, ' +
           'one lowercase letter, one number, and one special character';
  }

  /**
   * Returns an array of requirement descriptions for display
   */
  static getRequirements(): string[] {
    return [
      'At least 8 characters',
      'One uppercase letter (A-Z)',
      'One lowercase letter (a-z)',
      'One number (0-9)',
      'One special character (!@#$%^&*()_+-=[]{}|,.<>?)'
    ];
  }
}